import { BreakpointObserver } from "@angular/cdk/layout";
import { Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FX_MEDIA_QUERIES, FxBreakpoint, pickResponsiveValue } from "./breakpoints";

@Directive({
  standalone: false,
  selector: "[fxFlex]"
})
export class FxFlexDirective implements OnInit, OnChanges, OnDestroy {
  @Input() fxFlex?: string;
  @Input("fxFlex.xs") fxFlexXs?: string;
  @Input("fxFlex.sm") fxFlexSm?: string;
  @Input("fxFlex.md") fxFlexMd?: string;
  @Input("fxFlex.gt-xs") fxFlexGtXs?: string;
  @Input("fxFlex.gt-sm") fxFlexGtSm?: string;
  @Input("fxFlex.gt-md") fxFlexGtMd?: string;

  private readonly destroy$ = new Subject<void>();
  private readonly activeBreakpoints = new Set<FxBreakpoint>();

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.breakpointObserver
      .observe(Object.values(FX_MEDIA_QUERIES))
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.activeBreakpoints.clear();
        (Object.keys(FX_MEDIA_QUERIES) as FxBreakpoint[]).forEach((breakpoint) => {
          if (state.breakpoints[FX_MEDIA_QUERIES[breakpoint]]) {
            this.activeBreakpoints.add(breakpoint);
          }
        });
        this.applyFlex();
      });

    this.applyFlex();
  }

  ngOnChanges(_: SimpleChanges): void {
    this.applyFlex();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyFlex(): void {
    const rawValue = pickResponsiveValue(
      this.fxFlex,
      {
        "xs": this.fxFlexXs,
        "sm": this.fxFlexSm,
        "md": this.fxFlexMd,
        "gt-xs": this.fxFlexGtXs,
        "gt-sm": this.fxFlexGtSm,
        "gt-md": this.fxFlexGtMd
      },
      (breakpoint) => this.activeBreakpoints.has(breakpoint)
    );

    const value = (rawValue || "").trim();
    if (!value) {
      this.renderer.setStyle(this.host.nativeElement, "flex", "1 1 0%");
      this.renderer.removeStyle(this.host.nativeElement, "max-width");
      this.renderer.removeStyle(this.host.nativeElement, "width");
      return;
    }

    if (value.toLowerCase() === "none") {
      this.renderer.setStyle(this.host.nativeElement, "flex", "0 0 auto");
      this.renderer.removeStyle(this.host.nativeElement, "max-width");
      this.renderer.removeStyle(this.host.nativeElement, "width");
      return;
    }

    const basis = this.normalizeBasis(value);
    this.renderer.setStyle(this.host.nativeElement, "box-sizing", "border-box");
    this.renderer.setStyle(this.host.nativeElement, "flex", `0 0 ${basis}`);

    if (basis.endsWith("%") || basis.endsWith("px") || basis.endsWith("rem") || basis.endsWith("em")) {
      this.renderer.setStyle(this.host.nativeElement, "max-width", basis);
      this.renderer.setStyle(this.host.nativeElement, "width", basis);
    } else {
      this.renderer.removeStyle(this.host.nativeElement, "max-width");
      this.renderer.removeStyle(this.host.nativeElement, "width");
    }
  }

  private normalizeBasis(value: string): string {
    const trimmed = value.trim();
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      return `${trimmed}%`;
    }

    return trimmed;
  }
}
